import SuperTokens from "supertokens-web-js";
import Session from "supertokens-web-js/recipe/session";
import Passwordless from "supertokens-web-js/recipe/passwordless";

/**
 * SuperTokens wiring for the admin panel — the same passwordless email-OTP flow
 * immersive-web uses (`lib/auth/supertokens.ts`), against the same server.
 *
 * The panel talks to the API at a *relative* path (`/api/v2`, proxied by Vite in
 * dev), so `apiDomain` is this page's own origin rather than a server hostname —
 * that keeps auth same-origin with every other request the panel makes and means
 * there is no CORS surface to configure for the admin origin.
 *
 * `tokenTransferMethod: 'header'` is not a preference: the server pins
 * `getTokenTransferMethod: () => 'header'` (`immersive-server/src/lib/supertokens.config.ts`),
 * so a cookie-mode frontend would authenticate exactly zero requests.
 */
let initialized = false;

export function ensureSuperTokens(): void {
  if (initialized || typeof window === "undefined") {
    return;
  }
  SuperTokens.init({
    appInfo: {
      appName: "Immersive Admin",
      apiDomain: window.location.origin,
      apiBasePath: "/api/v2/auth",
    },
    recipeList: [
      Session.init({
        tokenTransferMethod: "header",
        maxRetryAttemptsForSessionRefresh: 3,
      }),
      Passwordless.init(),
    ],
  });
  initialized = true;
}

export function sessionExists(): Promise<boolean> {
  ensureSuperTokens();
  return Session.doesSessionExist();
}

/** Sends the 6-digit code. Throws with the server's reason if sign-in is refused. */
export async function sendLoginCode(email: string): Promise<void> {
  ensureSuperTokens();
  const result = await Passwordless.createCode({ email });
  if (result.status === "SIGN_IN_UP_NOT_ALLOWED") {
    throw new Error(result.reason);
  }
}

export async function resendLoginCode(): Promise<void> {
  ensureSuperTokens();
  const result = await Passwordless.resendCode();
  if (result.status === "RESTART_FLOW_ERROR") {
    throw new Error("Login attempt expired. Start over with your email.");
  }
}

export type ConsumeCodeOutcome = "ok" | "incorrect" | "expired" | "restart";

export async function consumeLoginCode(
  code: string
): Promise<ConsumeCodeOutcome> {
  ensureSuperTokens();
  const result = await Passwordless.consumeCode({ userInputCode: code });
  switch (result.status) {
    case "OK":
      return "ok";
    case "INCORRECT_USER_INPUT_CODE_ERROR":
      return "incorrect";
    case "EXPIRED_USER_INPUT_CODE_ERROR":
      return "expired";
    default:
      return "restart";
  }
}

export async function hasPendingLoginAttempt(): Promise<boolean> {
  ensureSuperTokens();
  return (await Passwordless.getLoginAttemptInfo()) !== undefined;
}

export async function clearLoginAttempt(): Promise<void> {
  ensureSuperTokens();
  await Passwordless.clearLoginAttemptInfo();
}

export async function signOut(): Promise<void> {
  ensureSuperTokens();
  await Session.signOut();
}
